import { useRef, useEffect, useState, useCallback } from "react";

interface ParallaxProps {
  children: React.ReactNode;
  className?: string;
  offset?: number;
  offsetX?: number;
  scaleFrom?: number;
  opacityFrom?: number;
}

const Parallax = ({
  children,
  className = "",
  offset = -40,
  offsetX = 0,
  scaleFrom,
  opacityFrom,
}: ParallaxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const ticking = useRef(false);

  const update = useCallback(() => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner) return;

    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    // progress: 0 when element enters bottom, 1 when it exits top
    const progress = Math.min(1, Math.max(0, 1 - (rect.top + rect.height) / (vh + rect.height)));

    const y = offset + ((-offset - offset) * progress);
    const x = offsetX !== 0 ? offsetX + ((-offsetX - offsetX) * progress) : 0;

    let transform = `translate3d(${x}px, ${y}px, 0)`;
    if (scaleFrom !== undefined) {
      const s = progress < 0.3 
        ? scaleFrom + (1 - scaleFrom) * (progress / 0.3)
        : progress > 0.7
        ? 1 - (1 - scaleFrom) * ((progress - 0.7) / 0.3)
        : 1;
      transform += ` scale(${s})`;
    }
    inner.style.transform = transform;

    if (opacityFrom !== undefined) {
      const o = progress < 0.2
        ? opacityFrom + (1 - opacityFrom) * (progress / 0.2)
        : progress > 0.8
        ? 1 - (1 - opacityFrom) * ((progress - 0.8) / 0.2)
        : 1;
      inner.style.opacity = String(o);
    }

    ticking.current = false;
  }, [offset, offsetX, scaleFrom, opacityFrom]);

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update(); // initial position
    return () => window.removeEventListener("scroll", onScroll);
  }, [update]);

  return (
    <div ref={ref} className={className}>
      <div ref={innerRef} style={{ willChange: "transform" }}>
        {children}
      </div>
    </div>
  );
};

export default Parallax;
